import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import MainLayout from '../../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../../shell/backofficeSidebar';
import { useAuth } from '../../../../../context/AuthContext';
import { resolveApiErrorMessage } from '../../../../../utils/apiErrorMessage';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type PayrollEmployeeRow = {
  id: string;
  employeeCode: string;
  fullName: string;
  workEmail?: string | null;
  salaryCurrency: string;
  baseSalary: number;
  totals: { paid: number; advances: number; deductions: number; bonuses: number; adjustments: number; netPaid: number; balance: number };
  entriesCount: number;
  departments: Array<{ id: string; name: string; slug: string }>;
};
type PayrollOverview = {
  month: string;
  count: number;
  period?: { status: 'OPEN' | 'CLOSED'; closedAt?: string | null };
  summary: { expectedPayroll: number; netPaid: number; balance: number; pendingEntries?: number };
  employees: PayrollEmployeeRow[];
};

function currentMonthValue() {
  const now = new Date();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  return `${now.getFullYear()}-${mm}`;
}

export default function PayrollPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const { authorizedFetch } = useAuth();
  const [month, setMonth] = useState(currentMonthValue());
  const [overview, setOverview] = useState<PayrollOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statement, setStatement] = useState<any | null>(null);
  const [statementLoading, setStatementLoading] = useState(false);
  const [periodSaving, setPeriodSaving] = useState(false);
  const [entryType, setEntryType] = useState<'SALARY' | 'ADVANCE' | 'FULL_PAYMENT' | 'DEDUCTION' | 'BONUS' | 'ADJUSTMENT'>('FULL_PAYMENT');
  const [entryAmount, setEntryAmount] = useState('');
  const [entryNote, setEntryNote] = useState('');

  const loadOverview = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('month', month);
      const res = await authorizedFetch(`${API_BASE}/auth/workspace/payroll/overview?${params.toString()}`);
      if (!res.ok) throw new Error(await resolveApiErrorMessage(res, 'Unable to load payroll overview.'));
      setOverview(await res.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load payroll overview.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  const money = (value: number, currency = 'USD') =>
    new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(value || 0);

  const rows = useMemo(() => overview?.employees || [], [overview]);

  const openStatement = async (employeeId: string) => {
    setStatementLoading(true);
    setError(null);
    try {
      const res = await authorizedFetch(
        `${API_BASE}/auth/workspace/payroll/employees/${employeeId}/statement?month=${encodeURIComponent(month)}`,
      );
      if (!res.ok) throw new Error(await resolveApiErrorMessage(res, 'Unable to load statement.'));
      setStatement(await res.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load statement.');
    } finally {
      setStatementLoading(false);
    }
  };

  const approveEntry = async (entryId: string) => {
    setStatementLoading(true);
    try {
      const res = await authorizedFetch(`${API_BASE}/auth/workspace/payroll/entries/${entryId}/approve`, {
        method: 'POST',
      });
      if (!res.ok) throw new Error(await resolveApiErrorMessage(res, 'Unable to approve entry.'));
      if (statement?.employee?.id) await openStatement(statement.employee.id);
      await loadOverview();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to approve entry.');
    } finally {
      setStatementLoading(false);
    }
  };

  const closePeriod = async () => {
    setPeriodSaving(true);
    try {
      const res = await authorizedFetch(`${API_BASE}/auth/workspace/payroll/period/close`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ month }),
      });
      if (!res.ok) throw new Error(await resolveApiErrorMessage(res, 'Unable to close payroll month.'));
      await loadOverview();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to close payroll month.');
    } finally {
      setPeriodSaving(false);
    }
  };

  const reopenPeriod = async () => {
    setPeriodSaving(true);
    try {
      const res = await authorizedFetch(`${API_BASE}/auth/workspace/payroll/period/reopen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ month }),
      });
      if (!res.ok) throw new Error(await resolveApiErrorMessage(res, 'Unable to reopen payroll month.'));
      await loadOverview();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to reopen payroll month.');
    } finally {
      setPeriodSaving(false);
    }
  };

  const exportCsv = () => {
    if (!overview) return;
    const headers = ['Employee Code', 'Name', 'Email', 'Base Salary', 'Net Paid', 'Balance', 'Entries'];
    const lines = [headers.join(',')];
    overview.employees.forEach((row) => {
      const vals = [
        row.employeeCode,
        row.fullName,
        row.workEmail || '',
        String(row.baseSalary || 0),
        String(row.totals.netPaid || 0),
        String(row.totals.balance || 0),
        String(row.entriesCount || 0),
      ].map((v) => `"${String(v).replace(/"/g, '""')}"`);
      lines.push(vals.join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payroll-${overview.month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const printStatement = () => {
    window.print();
  };

  const addPaymentEntry = async () => {
    if (!statement?.employee?.id) return;
    const amount = Number(entryAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setError('Amount must be greater than 0.');
      return;
    }
    setStatementLoading(true);
    try {
      const res = await authorizedFetch(`${API_BASE}/auth/workspace/employees/${statement.employee.id}/salary/entry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entryType,
          amount,
          currency: statement.employee.salaryCurrency || 'USD',
          note: entryNote || undefined,
        }),
      });
      if (!res.ok) throw new Error(await resolveApiErrorMessage(res, 'Unable to create payroll entry.'));
      setEntryAmount('');
      setEntryNote('');
      await openStatement(statement.employee.id);
      await loadOverview();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to create payroll entry.');
    } finally {
      setStatementLoading(false);
    }
  };

  return (
    <MainLayout withSidebar sidebarItems={getBackofficeSidebarItems(t)} activePath={location.pathname}>
      <main className="workspace-page">
        <section>
          <h1 className="workspace-page-title">Payroll management</h1>
          <p className="workspace-page-subtitle text-slate-600">
            Monthly payroll control with advances, deductions, and final balance visibility.
          </p>
        </section>

        <section className="workspace-panel">
          <div className="grid gap-2 md:grid-cols-1">
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs" />
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => void loadOverview()} className="rounded-full border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700">Refresh</button>
            <button type="button" onClick={exportCsv} className="rounded-full border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700">
              Export CSV
            </button>
            {overview?.period?.status === 'CLOSED' ? (
              <button
                type="button"
                onClick={reopenPeriod}
                disabled={periodSaving}
                className="rounded-full border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700 disabled:opacity-60"
              >
                Reopen month
              </button>
            ) : (
              <button
                type="button"
                onClick={closePeriod}
                disabled={periodSaving}
                className="rounded-full border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700 disabled:opacity-60"
              >
                Close month
              </button>
            )}
          </div>
          {error ? <p className="mt-3 text-xs text-red-500">{error}</p> : null}
          {overview?.period?.status === 'CLOSED' ? (
            <p className="mt-2 text-xs font-semibold text-amber-700">
              This payroll month is CLOSED. New entries and approvals are locked.
            </p>
          ) : (
            <p className="mt-2 text-xs text-slate-500">
              Payroll is global and not department-scoped. Approving entries posts to active budget.
            </p>
          )}
        </section>

        {overview && (
          <section className="grid gap-3 md:grid-cols-4">
            <div className="glass-panel p-4 text-sm">Expected payroll: <strong>{money(overview.summary.expectedPayroll)}</strong></div>
            <div className="glass-panel p-4 text-sm">Net paid: <strong>{money(overview.summary.netPaid)}</strong></div>
            <div className="glass-panel p-4 text-sm">Outstanding balance: <strong>{money(overview.summary.balance)}</strong></div>
            <div className="glass-panel p-4 text-sm">Pending approvals: <strong>{overview.summary.pendingEntries || 0}</strong></div>
          </section>
        )}

        <section className="workspace-panel">
          {loading ? (
            <p className="text-sm text-slate-600">{t('common.loading', 'Loading…')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-xs sm:text-sm">
                <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="py-2 pr-4">Employee</th>
                    <th className="py-2 pr-4">Base salary</th>
                    <th className="py-2 pr-4">Net paid</th>
                    <th className="py-2 pr-4">Balance</th>
                    <th className="py-2 pr-4">Entries</th>
                    <th className="py-2 pr-4" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td className="py-2 pr-4">
                        <div className="font-medium text-slate-900">{row.fullName}</div>
                        <div className="text-[11px] text-slate-500">{row.employeeCode} {row.workEmail ? `• ${row.workEmail}` : ''}</div>
                        <div className="mt-0.5 text-[11px] text-slate-500">
                          {(row.departments || []).slice(0, 2).map((dep) => dep.name).join(', ') || 'No department'}
                          {row.departments.length > 2 ? ` +${row.departments.length - 2} more` : ''}
                        </div>
                      </td>
                      <td className="py-2 pr-4">{money(row.baseSalary, row.salaryCurrency)}</td>
                      <td className="py-2 pr-4">{money(row.totals.netPaid, row.salaryCurrency)}</td>
                      <td className={`py-2 pr-4 font-medium ${row.totals.balance > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                        {money(row.totals.balance, row.salaryCurrency)}
                      </td>
                      <td className="py-2 pr-4">{row.entriesCount}</td>
                      <td className="py-2 pr-4">
                        <button type="button" onClick={() => void openStatement(row.id)} className="rounded-full border border-slate-300 bg-white px-3 py-1 text-[11px] font-semibold text-slate-700 hover:border-slate-400">
                          Statement
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!rows.length ? <p className="pt-3 text-xs text-slate-500">No payroll rows for this month.</p> : null}
            </div>
          )}
        </section>
      </main>

      {statement && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={() => setStatement(null)}>
          <div className="w-full max-w-3xl rounded-2xl bg-white p-5 shadow-xl sm:p-6" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">Payroll statement</h3>
                <p className="text-xs text-slate-600">{statement.employee?.fullName} • {statement.month}</p>
              </div>
              <button type="button" onClick={() => setStatement(null)} className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-[11px] font-semibold text-slate-700 hover:border-slate-400">
                {t('common.close', 'Close')}
              </button>
            </div>
            {statementLoading ? <p className="text-xs text-slate-500">Loading…</p> : null}
            <div className="mb-3 grid gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3 md:grid-cols-4">
              <select
                value={entryType}
                onChange={(e) => setEntryType(e.target.value as any)}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs"
              >
                <option value="FULL_PAYMENT">Full payment</option>
                <option value="SALARY">Salary</option>
                <option value="ADVANCE">Advance</option>
                <option value="DEDUCTION">Deduction</option>
                <option value="BONUS">Bonus</option>
                <option value="ADJUSTMENT">Adjustment</option>
              </select>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={entryAmount}
                onChange={(e) => setEntryAmount(e.target.value)}
                placeholder="Amount"
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs"
              />
              <input
                type="text"
                value={entryNote}
                onChange={(e) => setEntryNote(e.target.value)}
                placeholder="Payment note"
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs md:col-span-2"
              />
              <button
                type="button"
                onClick={() => void addPaymentEntry()}
                className="rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white hover:bg-slate-800"
              >
                Record payroll entry
              </button>
            </div>
            <div className="grid gap-2 pb-3 md:grid-cols-3">
              <div className="rounded-lg bg-slate-50 p-3 text-xs">Base: {money(statement.employee?.baseSalary || 0, statement.employee?.salaryCurrency || 'USD')}</div>
              <div className="rounded-lg bg-emerald-50 p-3 text-xs">Net paid: {money(statement.totals?.netPaid || 0, statement.employee?.salaryCurrency || 'USD')}</div>
              <div className="rounded-lg bg-amber-50 p-3 text-xs">Balance: {money(statement.totals?.balance || 0, statement.employee?.salaryCurrency || 'USD')}</div>
            </div>
            <div className="mb-3 flex items-center justify-end">
              <button type="button" onClick={printStatement} className="rounded-full border border-slate-300 bg-white px-3 py-1 text-[11px] font-semibold text-slate-700">
                Print payslip
              </button>
            </div>
            <div className="max-h-[45vh] overflow-auto rounded-xl border border-slate-200">
              <table className="min-w-full text-left text-xs">
                <thead className="border-b border-slate-200 bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-3 py-2">Date</th>
                    <th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2">Amount</th>
                    <th className="px-3 py-2">Note</th>
                    <th className="px-3 py-2">Approval</th>
                  </tr>
                </thead>
                <tbody>
                  {(statement.entries || []).map((entry: any) => (
                    <tr key={entry.id} className="border-b border-slate-100">
                      <td className="px-3 py-2 text-slate-600">{new Date(entry.occurredAt).toLocaleString()}</td>
                      <td className="px-3 py-2 font-medium">{entry.entryType}</td>
                      <td className="px-3 py-2">{money(Number(entry.amount), entry.currency)}</td>
                      <td className="px-3 py-2 text-slate-600">{entry.note || '—'}</td>
                      <td className="px-3 py-2">
                        {entry.approvedAt ? (
                          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                            Approved
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => void approveEntry(entry.id)}
                            className="rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700"
                          >
                            Approve
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
}

